import { clsx } from "clsx";
import { forwardRef, Fragment, type PropsWithChildren, useRef } from "react";
import { type AriaTabListProps } from "react-aria";

import { useScroll } from "../../hooks.ts";
import { useTabList } from "../../hooks/useTabList.tsx";
import type { AllTabDialogProps, CommonDialogProps, DialogProps, TabPopoverProps } from "../../types/dialogs.d.ts";
import { HeaderBar, type HeaderBarProps } from "../HeaderBar/HeaderBar.tsx";
import { type TabItemElement, TabListBar } from "../Tabs/TabListBar.tsx";
import { TabPanel } from "../Tabs/TabPanel.tsx";
import { DialogButtonsWithErrorCallout } from "./DialogButtonsWithErrorCallout.tsx";
import style from "./TabDialog.module.css";
import { useDialogHeaderActionsWithClose } from "./useDialogHeaderActionsWithClose.tsx";

type TabDialogComponentProps = AllTabDialogProps & { domTitleProps?: HeaderBarProps["domTitleProps"] };

export const TabDialogComponent = forwardRef<HTMLDivElement, TabDialogComponentProps>(function TabDialogComponent(
  props: TabDialogComponentProps,
  ref,
) {
  const {
    ref: tabRef,
    state,
    tabListProps,
  } = useTabList<HTMLDivElement, TabItemElement>(props as PropsWithChildren<AriaTabListProps<TabItemElement>>);
  const actions = useDialogHeaderActionsWithClose(props as CommonDialogProps);

  const scrollRef = useRef<HTMLDivElement>(null);
  useScroll(scrollRef);

  return (
    <div
      ref={ref}
      {...props.domProps}
      className={clsx(
        style.dialog,
        {
          [style.centered]: !!(props as DialogProps).underlayProps,
          [style.fullscreen]: props.fullscreen,
        },
        props.className,
      )}
      style={{ ...(props.domProps?.style || {}), ...props.style, width: props.width }}
      data-testid={"dialog"}
    >
      <div className={style.rounded_container}>
        <HeaderBar {...props} domTitleProps={props.domTitleProps} actions={actions} />
        <TabListBar state={state} tabListProps={tabListProps} ref={tabRef} className={style.dialog_tab} />
        {props.keepAlive ? (
          <TabPanel state={state} className={style.panel} keepAlive>
            {(children) => children.map((child) => <Fragment key={child.key}>{child.children}</Fragment>)}
          </TabPanel>
        ) : (
          <TabPanel state={state} className={style.panel} key={state.selectedKey}>
            {({ children, key }) => <Fragment key={key}>{children}</Fragment>}
          </TabPanel>
        )}

        {props.mainButton != undefined && (
          <DialogButtonsWithErrorCallout {...props} className={clsx(props.className, style.footer_bar)} />
        )}
      </div>
      {(props as TabPopoverProps).arrowProps != undefined && (
        <div
          className={style.arrow}
          data-placement={(props as TabPopoverProps).placement}
          {...(props as TabPopoverProps).arrowProps}
        />
      )}
    </div>
  );
});
